import type { IntervalResult, TrackingActivity, TrackingInterval } from "../contracts";
import { RecoveryCoordinator } from "../recovery/coordinator";
import type { RecoveryOptions } from "../recovery/contracts";
import { recallIntervalResults, rememberIntervalResult, type ResultCacheScope } from "./result-cache";
import { SessionRuntime, type SessionConfig, type SessionDependencies } from "./session-runtime";

export interface TrackingSnapshot {
  results: ReadonlyMap<string, IntervalResult>;
  resultsVersion: number;
  errors: ReadonlyMap<string, string>;
  activity: TrackingActivity;
}

const errorText = (error: unknown) => (error instanceof Error ? error.message : String(error));

/** Public per-player state and recovery orchestration; execution belongs to SessionRuntime. */
export class TrackingSession {
  private readonly config: SessionConfig;
  private readonly runtime: SessionRuntime;
  private intervals: readonly TrackingInterval[] = [];
  private intervalIds = new Set<string>();
  private execution: object | null = null;
  private listeners = new Set<() => void>();
  private notificationQueued = false;
  private snapshot: TrackingSnapshot;
  private baseResults = new Map<string, IntervalResult>();
  private readonly recovery: RecoveryCoordinator;
  private recoveryKey: string | undefined;

  /** Construction allocates no workers; configuration belongs to this session's lifetime. */
  constructor(config: SessionConfig, dependencies: Partial<SessionDependencies> = {}) {
    this.config = {
      ...config,
      options: {
        ...config.options,
        strategies: { ...config.options.strategies },
        nanoModelUrls: { ...config.options.nanoModelUrls },
      },
    };
    this.snapshot = this.emptySnapshot(0);
    this.runtime = new SessionRuntime(this.config, dependencies, {
      activity: (patch) => this.publish({ activity: { ...this.snapshot.activity, ...patch } }),
      progress: (intervalId, workerId, stage) =>
        this.publish({
          activity: {
            ...this.snapshot.activity,
            active: new Map(this.snapshot.activity.active).set(intervalId, { intervalId, workerId, stage }),
          },
        }),
      result: (result, complete) => this.save(result, complete),
      finish: (intervalId) => {
        const active = new Map(this.snapshot.activity.active);
        active.delete(intervalId);
        this.publish({ activity: { ...this.snapshot.activity, active } });
      },
      error: (id, error) => this.recordError(id, error),
      unavailable: (error) => {
        const reason = errorText(error);
        this.publish({
          errors: new Map(this.snapshot.errors).set("worker", reason),
          activity: { ...this.snapshot.activity, unavailableReason: reason },
        });
      },
    });
    this.recovery = new RecoveryCoordinator({
      extract: (base, targetTime, signal) => this.runtime.extractRecovery(base, targetTime, signal),
      refine: (parent, children, signal) => this.runtime.refineRecovery(parent, children, signal),
      publish: (result, final) => {
        if (!this.execution || !this.intervalIds.has(result.interval.id)) return;
        // Only the composed result is retained. The direct publish shows the
        // recovered anchor while its gap is still being refined, and recalling
        // one would carry recovery lineage without the frames refinement fills.
        if (final) rememberIntervalResult(this.cacheScope(), result);
        this.publish({
          results: new Map(this.snapshot.results).set(result.interval.id, result),
          resultsVersion: this.snapshot.resultsVersion + 1,
        });
      },
      changed: (recovery) => this.publish({ activity: { ...this.snapshot.activity, recovery } }),
    });
  }

  getSnapshot = (): TrackingSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Appearance and recovery changes never recreate local workers or discard their predictions. */
  configureRecovery(options: RecoveryOptions, labels?: ReadonlyMap<string, string>): void {
    // Part of the cache scope: recovery-refined frames belong to one output run.
    this.recoveryKey = options.enabled ? options.provider?.key : undefined;
    if (!this.recovery.configure(options, labels)) return;
    this.publish({ results: new Map(this.baseResults), resultsVersion: this.snapshot.resultsVersion + 1 });
    if (!this.execution) return;
    for (const result of this.baseResults.values()) this.recovery.offer(result);
  }

  /** Cleanup belongs to this execution, so a stale cleanup cannot stop a restarted session. */
  start(): () => void {
    if (this.execution) throw new Error("Tracking session is already started");
    const execution = {};
    this.execution = execution;
    this.baseResults.clear();
    this.recovery.cancel();
    this.recovery.activate();
    this.publish(this.emptySnapshot(this.snapshot.resultsVersion + 1));
    this.publish({ activity: { ...this.snapshot.activity, recovery: this.recovery.getActivity() } });
    // Adopt cached results before the runtime admits anything, so known
    // intervals are never dispatched in the first batch.
    const stopRuntime = this.runtime.start(() => this.hydrateFromCache());
    return () => {
      if (this.execution !== execution) return;
      this.recovery.cancel();
      stopRuntime();
      this.execution = null;
    };
  }

  /** This session's cache identity, so a rerun can discard what it supersedes. */
  get scope(): ResultCacheScope {
    return this.cacheScope();
  }

  private cacheScope(): ResultCacheScope {
    return {
      src: this.config.src,
      options: this.config.options,
      recoveryKey: this.recoveryKey,
      runKey: this.config.runKey,
    };
  }

  /**
   * Adopt completed intervals this source has already produced in an earlier
   * session, so remounting the player (switching assets and back, say) shows
   * finished tracks instead of decoding the video again.
   */
  private hydrateFromCache(): void {
    if (!this.execution || !this.intervals.length) return;
    const missing = this.intervals.map((interval) => interval.id).filter((id) => !this.baseResults.has(id));
    if (!missing.length) return;
    const recalled = recallIntervalResults(this.cacheScope(), missing);
    if (!recalled.size) return;

    const results = new Map(this.snapshot.results);
    const completed = new Set(this.snapshot.activity.completed);
    const errors = new Map(this.snapshot.errors);
    for (const [id, result] of recalled) {
      this.baseResults.set(id, result);
      results.set(id, result);
      completed.add(id);
      // A recalled interval supersedes whatever failed here, exactly as
      // completing one locally does.
      errors.delete(id);
    }
    // Keep the scheduler from re-queuing them, and let "all processed" account
    // for them as done.
    this.runtime.markAttempted(recalled.keys());
    this.publish({
      results,
      errors,
      resultsVersion: this.snapshot.resultsVersion + 1,
      activity: { ...this.snapshot.activity, completed },
    });
    // Refining re-tracks the interval's two halves around the recovered anchor,
    // so an already refined result must not be offered again: candidacy is about
    // gaps that remain, and any the refinement could not fill still read as one.
    // Only composed results are cached, so lineage here means refinement ran to
    // completion; an interrupted one recalls its base and is offered normally.
    for (const [, result] of recalled) {
      if (!result.frames.some((frame) => frame.recovery)) this.recovery.offer(result);
    }
  }

  /** Commit anchors and playhead together; seeks alone never reconcile or discard cached boxes. */
  update(intervals: readonly TrackingInterval[], playhead: number): void {
    this.runtime.updatePlayhead(playhead);
    this.recovery.reconcile(intervals, playhead);
    if (this.intervals === intervals) {
      this.runtime.pumpCurrent();
      return;
    }
    const hadIntervals = this.intervals.length > 0;
    this.intervals = intervals;
    this.intervalIds = new Set(intervals.map((interval) => interval.id));
    const removed = (id: string) => !this.intervalIds.has(id);
    for (const id of this.baseResults.keys()) if (removed(id)) this.baseResults.delete(id);
    this.runtime.reconcile(intervals);
    this.hydrateFromCache();
    const { results, errors, activity } = this.snapshot;
    const nextResults = [...results.keys()].some(removed)
      ? new Map([...results].filter(([id]) => !removed(id)))
      : results;
    const nextErrors = [...errors.keys()].some((id) => id !== "worker" && removed(id))
      ? new Map([...errors].filter(([id]) => id === "worker" || !removed(id)))
      : errors;
    const active = [...activity.active.keys()].some(removed)
      ? new Map([...activity.active].filter(([id]) => !removed(id)))
      : activity.active;
    const completed = [...activity.completed].some(removed)
      ? new Set([...activity.completed].filter((id) => !removed(id)))
      : activity.completed;
    if (
      nextResults !== results ||
      nextErrors !== errors ||
      active !== activity.active ||
      completed !== activity.completed
    ) {
      this.publish({
        results: nextResults,
        resultsVersion: this.snapshot.resultsVersion + (nextResults !== results ? 1 : 0),
        errors: nextErrors,
        activity: { ...activity, active, completed },
      });
    }
    if (!intervals.length && hadIntervals) {
      this.runtime.stopWorkers();
      // An empty timeline ends this worker run; later streamed anchors may start afresh.
      if (this.snapshot.errors.size || this.snapshot.activity.unavailableReason) {
        this.publish({ errors: new Map(), activity: { ...this.snapshot.activity, unavailableReason: undefined } });
      }
    }
    this.runtime.ensure();
  }

  private emptySnapshot(resultsVersion: number): TrackingSnapshot {
    return {
      results: new Map(),
      resultsVersion,
      errors: new Map(),
      activity: { enabled: this.config.enabled, active: new Map(), completed: new Set(), workerCount: 0 },
    };
  }

  private publish(patch: Partial<TrackingSnapshot>): void {
    if (patch.activity) patch.activity = { ...patch.activity, recovery: this.recovery.getActivity() };
    this.snapshot = { ...this.snapshot, ...patch };
    // Snapshots are immediately readable, but callbacks must not interrupt
    // admission, reconciliation, or shutdown while their state is incomplete.
    if (this.notificationQueued || !this.listeners.size) return;
    this.notificationQueued = true;
    queueMicrotask(() => {
      this.notificationQueued = false;
      // Deliver the latest snapshot, including a run restarted before this
      // microtask. Subscriber mutations schedule a later notification.
      for (const listener of [...this.listeners]) {
        if (this.listeners.has(listener)) listener();
      }
    });
  }

  private save(result: IntervalResult, complete: boolean): void {
    this.baseResults.set(result.interval.id, result);
    // Only finished intervals are retained: a provisional sparse preview would
    // otherwise be recalled later as though it were final.
    if (complete) rememberIntervalResult(this.cacheScope(), result);
    let errors = this.snapshot.errors;
    if (complete) {
      const nextErrors = new Map(errors);
      nextErrors.delete(result.interval.id);
      errors = nextErrors;
    }
    this.publish({
      results: new Map(this.snapshot.results).set(result.interval.id, result),
      resultsVersion: this.snapshot.resultsVersion + 1,
      errors,
      activity: complete
        ? { ...this.snapshot.activity, completed: new Set(this.snapshot.activity.completed).add(result.interval.id) }
        : this.snapshot.activity,
    });
    // Queue recovery in this publication turn, before observers can see a terminal gap.
    if (complete) this.recovery.offer(result);
  }

  private recordError(id: string, error: unknown): void {
    this.publish({ errors: new Map(this.snapshot.errors).set(id, errorText(error)) });
  }
}
